//================================================================
//  Component: PTE RHF Lookup
//================================================================

//  Purpose: This is an input field that allows you to search 'oracleProjects' collection for PTOs

//  Properties:
//    - required = {boolean, this determines if this field is required}
//    - register = {react hooks forms state, connects the field to the form state}
//    - setValue = {react hooks form state, programmatically sets the value for a field}
//    - errors = {react hooks form state, stores the errors for the field}
//    - clearErrors = {react hooks form state, allows you to clear errors on a certain field}
//    - disabled = <Optional> {boolean, determines if the field is disabled}
//    - watch = {react hooks form state, allows you to watch a certain field}

//  Example:
//
//      <PTELookupRHF
//          required={false}
//          register={register}
//          setValue={setValue}
//          errors={errors}
//          clearErrors={clearErrors}
//          disabled={false}
//          watch={watch}
//      ></PTELookupRHF> 

//  IMPORTANT: Standard fieldId's are returned as part of this component, these are:
//      
//     'projectcode', 'tasknumber' & 'expendituretype'
//

//================================================================


//Libraries
import React, { useReducer, useEffect, useContext } from 'react';

//Contexts
import { SetToast } from '../../Library/GlobalContexts';

//Components

//Functions
import GetDocument from '../../Library/GetDocument';

//Images


export default function PTELookupRHF({
    required,
    register,
    setValue,
    errors,
    clearErrors,
    disabled,
    watch
}) {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'searchInput': '', // 
            'queryMessage': '',
            'taskNumbers': [],
            'inputDisabled': disabled
        }
    );

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {

        if (clearErrors === undefined) return;

        clearErrors('projectcode');
        clearErrors('tasknumber');
        clearErrors('expendituretype');

    }, [clearErrors]);

    // Watch the value of projectcode and trigger handleSearch
    const projectCode = watch('projectcode'); // Use watch to observe changes in projectcode

    useEffect(() => {
        if (projectCode) {
            handleSearch(projectCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectCode]);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    const handleSearch = (value) => {

        if (value.length < 6) return setSearchData({ 'taskNumbers': [] }); // perform search AFTER 6 characters

        setSearchData({ 'inputDisabled': true }); // disable input, allow fetching of data...

        // Search by characters in email
        GetDocument('oracleProjects', value, true).then((doc) => {

            // No PTE found
            if (doc === undefined) {


                setSearchData({
                    'taskNumbers': [],
                    'inputDisabled': false,
                });
                setValue('tasknumber', '');
                setValue('expendituretype', '');

                return;

            }
            const taskNumbers = Object.keys(doc.taskNumbers); // taskNumbers are arrays?!

            // Sort to decending order
            taskNumbers.sort();

            // Found PTE
            setSearchData({
                'taskNumbers': taskNumbers,
                'inputDisabled': false,
            });

            // clear errors for projectcode
            clearErrors('projectcode');

        }).catch((error) => {

            console.log(error);

            setSearchData({
                'taskNumbers': [],
                'inputDisabled': false,
            });
            setValue('tasknumber', '');
            setValue('expendituretype', '');

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Error fetching PTE data',
            });

        });

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col w-full h-full relative gap-2'>

            {/* ------------------------------------------------------ */}
            {/* Project Code - projectcode                             */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row'>

                <label className='w-[130px]'>Project Code</label>

                <input
                    className={errors.projectcode ? ('Input-Field-Text-Error  md:w-[450px]') : ('Input-Field-Text  md:w-[450px]')}
                    type='text'
                    placeholder={'Project Code'}
                    {...register('projectcode', {
                        required: required ? 'These fields are required' : false,
                    })}
                    onChange={(e) => {

                        setValue('projectcode', e.target.value);

                    }}
                    autoComplete='off'
                    disabled={searchData.inputDisabled}
                ></input>

            </div>



            {/* ------------------------------------------------------ */}
            {/* Task Number - tasknumber                               */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row'>

                <label className='w-[130px]'>Task Number</label>

                {
                    searchData.taskNumbers.length > 0 ?

                        // Found PTE doc --> Show task number options
                        <select
                            className='Input-Field-Select md:w-[450px]'
                            {...register('tasknumber')}
                            onChange={() => {

                                if (searchData.taskNumbers.length === 0) return;

                                setValue('expendituretype', 'ICT Costs');

                            }}
                        >
                            <option hidden value=''>Select one</option>
                            {
                                searchData.taskNumbers.map((option) => (
                                    <option value={option}>{option}</option>
                                ))
                            }

                        </select>

                        :

                        // No PTE doc found --> allow free text
                        <input
                            className='Input-Field-Text  md:w-[450px]'
                            type='text'
                            placeholder={'Task Number'}
                            {...register('tasknumber')}
                            autoComplete='off'
                            disabled={searchData.inputDisabled}
                        ></input>

                }

            </div>

            {/* ------------------------------------------------------ */}
            {/* ICT Costs - expendituretype                            */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row'>

                <label className='w-[130px]'>Expenditure Type</label>

                <input
                    className='Input-Field-Text  md:w-[450px]'
                    type='text'
                    placeholder={'ICT Costs'}
                    {...register('expendituretype')}
                    autoComplete='off'
                    disabled={searchData.inputDisabled}
                ></input>


            </div>

            {/* Error Message */}
            {errors.projectcode && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.projectcode.message}</p>}

        </div>
    )

}
